import { mapGetters } from 'vuex';

import { getDescendantProp } from '@/shared/modules/getDisplayValue';

import cropsToDropdownItems from './cropsToDropdownItems';

/**
 * ATTENTION, USE WITH CARE!
 * Always use this mixin together with shared/handsontable/mixins/containers/tableContainerBase.
 * This mixin maps the data in the crops store to Dropdown items.
 *
 * columnKeysToStore:
 * - key
 *   The column key for the column that gets the mapped data.
 * - value
 *   Is the sub-model key in the tableData object that can contain a crop.
 *   This is used to display crop info w/o loading the whole list from the BE.
 *
 * @param columnKeysToStore
 * @return Object
 */
export default function columnDropdownCrops(columnKeysToStore = {}) {
  return {
    data() {
      return {
        columnSettingsMiddleware: [],
      };
    },
    computed: {
      ...mapGetters({
        crops: 'products/crops',
        cropsUsed: 'products/crops/used',
      }),
      ...cropsToDropdownItems.computed,
    },
    created() {
      this.columnSettingsMiddleware.push(this.columnDropdownCropsColumnSettings);
      this.addHotHook('afterOnCellMouseOver', this.columnDropdownCropsAfterOnCellMouseOver);
      this.addHotHook('afterChange', this.columnDropdownCropsAfterChange);
    },
    methods: {
      columnDropdownCropsColumnSettings(columns) {
        return columns.map((column) => {
          if (!Object.keys(columnKeysToStore).includes(column.key)) {
            return column;
          }
          return {
            ...column,
            getRendererValue: (...args) =>
              this.columnDropdownCropsGetRendererValue(columnKeysToStore[column.key], ...args),
            getItems: (...args) => this.columnDropdownCropsGetItems(...args),
          };
        });
      },
      columnDropdownCropsGetRendererValue(objectKey, { value, visualRow, guid: guidArg }) {
        let guid = guidArg;
        if (guid == null) {
          guid = this.$refs.table.visualRowToGuid(visualRow);
        }
        let text = '';
        if (this.crops[value] != null && typeof this.crops[value].name === 'string') {
          text = this.crops[value].name;
        } else if (
          guid != null &&
          this.tableData[guid] != null &&
          getDescendantProp(this.tableData[guid], objectKey) != null &&
          typeof getDescendantProp(this.tableData[guid], objectKey).name === 'string'
        ) {
          text = getDescendantProp(this.tableData[guid], objectKey).name;
        }
        return text;
      },
      async columnDropdownCropsGetItems() {
        await this.$store.dispatch('products/crops/subscribe');
        return this.cropsToDropdownItems;
      },
      columnDropdownCropsAfterOnCellMouseOver(event, coords) {
        const physicalCol = this.hot.toPhysicalColumn(coords.col);
        if (Object.keys(columnKeysToStore).includes(this.tableSettings.columns[physicalCol].key)) {
          this.$store.dispatch('products/crops/subscribe');
        }
      },
      columnDropdownCropsAfterChange(changes, source) {
        if (!['edit', 'CopyPaste.paste'].includes(source) || !Array.isArray(changes)) {
          return;
        }
        changes.forEach(([, prop, , newCropGuid]) => {
          const physicalCol = this.hot.propToCol(prop);
          if (!Object.keys(columnKeysToStore).includes(this.tableSettings.columns[physicalCol].key)) {
            return;
          }
          if (this.crops[newCropGuid] != null) {
            this.$store.commit('products/crops/useItem', newCropGuid);
          }
        });
      },
    },
  };
}
